import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Users, MessageCircle, Send, CheckCircle2, Sparkles, Home as HomeIcon, Handshake } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { useI18n } from "../i18n/I18nContext";
import { api, formatApiError, formatPrice } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", whatsapp: "", message: "" };

export default function Booking() {
    const { t, lang } = useI18n();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [type, setType] = useState(params.get("type") === "meeting" ? "meeting" : "rental");
    const [form, setForm] = useState(EMPTY);
    const [rental, setRental] = useState({
        property_id: params.get("property_id") || "",
        check_in: "",
        check_out: "",
        guests: 1,
    });
    const [meetingSlot, setMeetingSlot] = useState("");
    const [properties, setProperties] = useState([]);
    const [slots, setSlots] = useState([]);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        api.get("/properties", { params: { transaction: "rent" } }).then((r) => setProperties(r.data)).catch(() => {});
        api.get("/meeting-slots").then((r) => setSlots(r.data)).catch(() => {});
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setR = (k, v) => setRental((r) => ({ ...r, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const base = { type, ...form };
            let payload;
            if (type === "rental") {
                const prop = properties.find((p) => p.id === rental.property_id);
                payload = {
                    ...base,
                    property_id: rental.property_id,
                    property_title: prop ? (lang === "en" ? prop.title_en || prop.title_fr : prop.title_fr) : "",
                    check_in: rental.check_in,
                    check_out: rental.check_out,
                    guests: Number(rental.guests) || 1,
                };
            } else {
                payload = { ...base, meeting_slot_id: meetingSlot };
            }
            const { data } = await api.post("/bookings", payload);
            toast.success(t("booking.success_title"));
            setSuccess(data);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) toast.error(type === "rental" ? t("booking.date_unavailable") : t("booking.slot_taken"));
            else toast.error(formatApiError(err) || t("booking.error"));
        } finally {
            setSending(false);
        }
    };

    if (success) {
        return (
            <PublicLayout>
                <section className="bg-brand-navy text-white py-24">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="booking-success" className="bg-white text-brand-navy p-14">
                            <CheckCircle2 className="w-14 h-14 text-brand-emerald mx-auto mb-6" strokeWidth={1.5} />
                            <h1 className="font-display text-4xl mb-3">{t("booking.success_title")}</h1>
                            <p className="text-gray-600 mb-8">{t("booking.success_desc")}</p>
                            <button
                                onClick={() => navigate(`/reservation/suivi/${success.tracking_token}`)}
                                data-testid="booking-track-btn"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                            >
                                {t("booking.track_link")}
                            </button>
                        </motion.div>
                    </div>
                </section>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="relative bg-brand-navy text-white overflow-hidden" data-testid="booking-hero">
                <div className="absolute inset-0 opacity-25 bg-grain" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="inline-flex items-center gap-3 text-brand-gold text-[0.7rem] tracking-[0.32em] uppercase font-semibold mb-5">
                        <Sparkles className="w-4 h-4" /> {t("booking.hero_kicker")}
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">{t("booking.hero_title")}</h1>
                    <p className="mt-5 text-white/75 max-w-2xl">{t("booking.hero_desc")}</p>
                </div>
            </section>

            <section className="py-16 bg-brand-paper">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Tabs */}
                    <div className="grid grid-cols-2 gap-2 mb-8" data-testid="booking-tabs">
                        <TabBtn active={type === "rental"} onClick={() => setType("rental")} icon={HomeIcon} label={t("booking.tab_rental")} testid="tab-rental" />
                        <TabBtn active={type === "meeting"} onClick={() => setType("meeting")} icon={Handshake} label={t("booking.tab_meeting")} testid="tab-meeting" />
                    </div>

                    <form onSubmit={submit} className="bg-white border border-gray-100 p-6 sm:p-10 space-y-6" data-testid="booking-form">
                        {type === "rental" ? (
                            <RentalFields rental={rental} setR={setR} properties={properties} t={t} lang={lang} />
                        ) : (
                            <MeetingFields slots={slots} value={meetingSlot} onChange={setMeetingSlot} t={t} lang={lang} />
                        )}
                        <div className="pt-2 grid sm:grid-cols-2 gap-5">
                            <Input label={t("booking.name")} value={form.name} onChange={(v) => setField("name", v)} required testid="bk-name" />
                            <Input label={t("booking.email")} type="email" value={form.email} onChange={(v) => setField("email", v)} required testid="bk-email" />
                            <Input label={t("booking.phone")} value={form.phone} onChange={(v) => setField("phone", v)} required testid="bk-phone" />
                            <Input label={t("booking.whatsapp")} value={form.whatsapp} onChange={(v) => setField("whatsapp", v)} testid="bk-whatsapp" />
                        </div>
                        <TextArea label={t("booking.message")} value={form.message} onChange={(v) => setField("message", v)} testid="bk-message" />
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="bk-submit"
                            className="inline-flex items-center gap-2 px-10 py-4 bg-brand-navy hover:bg-brand-gold text-white text-[0.72rem] tracking-[0.28em] uppercase font-semibold transition-colors disabled:opacity-60"
                        >
                            <Send className="w-4 h-4" /> {sending ? t("booking.submitting") : t("booking.submit")}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`px-6 py-4 flex items-center justify-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-all border ${
                active ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-gray-200 hover:border-brand-gold"
            }`}
        >
            <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
        </button>
    );
}

function RentalFields({ rental, setR, properties, t, lang }) {
    return (
        <div className="space-y-5">
            <label className="block">
                <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{t("booking.rental_property")}</span>
                <select
                    value={rental.property_id}
                    onChange={(e) => setR("property_id", e.target.value)}
                    required
                    data-testid="rental-property"
                    className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold"
                >
                    <option value="">{t("booking.rental_pick_property")}</option>
                    {properties.map((p) => (
                        <option key={p.id} value={p.id}>
                            {(lang === "en" ? p.title_en || p.title_fr : p.title_fr)} — {formatPrice(p.price, p.currency)}
                        </option>
                    ))}
                </select>
            </label>
            <div className="grid sm:grid-cols-3 gap-5">
                <Input label={t("booking.rental_check_in")} type="date" value={rental.check_in} onChange={(v) => setR("check_in", v)} required testid="rental-checkin" icon={CalendarDays} />
                <Input label={t("booking.rental_check_out")} type="date" value={rental.check_out} onChange={(v) => setR("check_out", v)} required testid="rental-checkout" icon={CalendarDays} />
                <Input label={t("booking.rental_guests")} type="number" value={rental.guests} onChange={(v) => setR("guests", v)} required testid="rental-guests" icon={Users} />
            </div>
        </div>
    );
}

function MeetingFields({ slots, value, onChange, t, lang }) {
    if (!slots.length) return <div className="text-center py-10 text-gray-500" data-testid="meeting-no-slots">{t("booking.meeting_no_slots")}</div>;
    return (
        <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-3">{t("booking.meeting_pick_slot")}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="meeting-slots">
                {slots.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => onChange(s.id)}
                        data-testid={`slot-${s.id}`}
                        className={`p-4 text-left border transition-all ${
                            value === s.id ? "border-brand-gold bg-brand-gold/10" : "border-gray-200 hover:border-brand-navy"
                        }`}
                    >
                        <div className="font-display text-lg text-brand-navy">{new Date(s.date).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { weekday: "short", day: "2-digit", month: "short" })}</div>
                        <div className="text-sm text-brand-emerald font-semibold">{s.start} – {s.end}</div>
                        {s.label ? <div className="text-xs text-gray-500 mt-1">{s.label}</div> : null}
                    </button>
                ))}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required, testid, icon: Icon }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy inline-flex items-center gap-1">
                {Icon ? <Icon className="w-3 h-3" /> : null}{label}{required ? <span className="text-brand-gold"> *</span> : null}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                data-testid={testid}
                className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors"
            />
        </label>
    );
}

function TextArea({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{label}</span>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={4}
                data-testid={testid}
                className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors resize-y min-h-[100px]"
            />
        </label>
    );
}
